import { useEffect, useState } from "react";
import axios from "axios";
import { revealProps } from "../common/Reveal";

export type SurveyRespondent = {
  user_id: string;
  name: string | null;
  email: string;
  rank: string | null;
  badge: string | null;
  completed_at: string | null;
  overall: number | null;
  has_quote: boolean;
};

interface Props {
  agencyId: string;
  // Annual reports only count surveys completed on or after this date
  // (YYYY-MM-DD); null for trial reports.
  windowStart: string | null;
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  selected: string[];
  onSelectedChange: (userIds: string[]) => void;
  disabled?: boolean;
}

const checkboxCls =
  "checkbox checkbox-sm [--chkbg:theme(colors.blue.500)] [--chkfg:white] checked:border-blue-500";

// "Only show certain officers" on the Findings tab: limits the report's
// officer survey section to hand-picked respondents. Every other number in
// the report still covers the full officer scope.
const FindingsSurveyOfficerPicker: React.FC<Props> = ({
  agencyId,
  windowStart,
  enabled,
  onEnabledChange,
  selected,
  onSelectedChange,
  disabled,
}) => {
  const [respondents, setRespondents] = useState<SurveyRespondent[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setRespondents(null);
    setError(null);
    axios
      .get<{ respondents: SurveyRespondent[] }>(
        `${process.env.REACT_APP_BACKEND_URL}/api/agency-management/agencies/${agencyId}/survey/respondents`,
        { params: windowStart ? { window_start: windowStart } : undefined }
      )
      .then((res) => {
        if (cancelled) return;
        setRespondents(res.data.respondents);
        // A changed review window can drop officers; never send ids the
        // list no longer shows.
        const ids = new Set(res.data.respondents.map((r) => r.user_id));
        const kept = selected.filter((id) => ids.has(id));
        if (kept.length !== selected.length) onSelectedChange(kept);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.error || "Failed to load survey respondents");
      });
    return () => {
      cancelled = true;
    };
    // Selection is pruned once per fetch, not re-fetched per tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, agencyId, windowStart]);

  const toggle = (userId: string) =>
    onSelectedChange(
      selected.includes(userId) ? selected.filter((id) => id !== userId) : [...selected, userId]
    );

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-base-content/80 mb-2">
        Officer survey
      </label>
      <label className="flex items-center gap-2 text-sm text-base-content cursor-pointer">
        <input
          type="checkbox"
          className={checkboxCls}
          checked={enabled}
          onChange={(e) => onEnabledChange(e.target.checked)}
          disabled={disabled}
        />
        Only show certain officers
      </label>
      <p className="text-xs text-base-content/60 mt-1 leading-relaxed">
        {enabled
          ? "The “What officers said” section (ratings, charts, and quotes) is built from only the officers you pick. Case counts, hours saved, the leaderboard, and the cost projection are unchanged."
          : "Every officer who completed the survey counts toward the “What officers said” section."}
      </p>

      {enabled && (
        <div className="mt-3 rounded-md border border-base-300">
          {error ? (
            <div className="px-4 py-3 text-sm text-red-600 dark:text-red-400">{error}</div>
          ) : respondents === null ? (
            <div className="px-4 py-3 text-sm text-base-content/60">Loading officers…</div>
          ) : respondents.length === 0 ? (
            <div className="px-4 py-3 text-sm text-base-content/60">
              No officer has completed the survey{windowStart ? " in this review window" : ""} yet.
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-3 border-b border-base-300 bg-base-200 px-4 py-2 text-xs">
                <span className="text-base-content/60">
                  {selected.length} of {respondents.length} selected
                </span>
                <span className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => onSelectedChange(respondents.map((r) => r.user_id))}
                    disabled={disabled}
                    className="text-blue-600 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-200 underline disabled:opacity-50"
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    onClick={() => onSelectedChange([])}
                    disabled={disabled}
                    className="text-blue-600 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-200 underline disabled:opacity-50"
                  >
                    Clear
                  </button>
                </span>
              </div>
              <ul className="max-h-64 overflow-y-auto divide-y divide-base-300">
                {respondents.map((r, i) => (
                  <li key={r.user_id} style={revealProps(i).style} className="reveal">
                    <label className="flex items-center gap-3 px-4 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-base-200">
                      <input
                        type="checkbox"
                        className={checkboxCls}
                        checked={selected.includes(r.user_id)}
                        onChange={() => toggle(r.user_id)}
                        disabled={disabled}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-base-content">
                          {[r.rank, r.name].filter(Boolean).join(" ") || r.email}
                        </span>
                        <span className="block truncate text-[11px] text-base-content/50">
                          {[
                            r.badge ? `#${r.badge}` : null,
                            r.completed_at
                              ? `answered ${new Date(r.completed_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`
                              : null,
                            r.has_quote ? "left a quote" : null,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      </span>
                      {r.overall !== null && (
                        <span className="shrink-0 text-xs tabular-nums text-base-content/60">
                          {r.overall} / 5
                        </span>
                      )}
                    </label>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default FindingsSurveyOfficerPicker;
